/* eslint-disable react/require-default-props */
import React, { useMemo, useContext } from 'react';
import proptypes from 'prop-types';
const { shape: shapeProps, arrayOf: arrayOfProps, bool: boolProps } = proptypes;
import {
  params, string, arrayOf, bool, shape, extend, QueryContext
} from '@thd-nucleus/data-sources';
import { Typography } from '@one-thd/sui-atomic-components';
import { Price } from '@thd-olt-component-react/price';
import { Promotion } from '@thd-olt-component-react/promotion';
import { useBundlePricingDetails } from '@thd-olt-component-react/product-bundle';
import { getBundlePromotionData } from '../utils/bundle-pricing';
import { NONE_VALUE } from '../constants/laundry';
import { LaundryAddToCart } from './laundry/LaundryAddToCart';

/**
 * LaundryPrice component displays the pricing details for a laundry bundle,
 * including selected products and accessories, and provides an option to add the bundle to the cart.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Object} props.bundle - The main bundle product object.
 * @param {Array<Object>} props.products - Array of selected product objects.
 * @param {Object} props.accessories - Accessories selection object, containing selectedVariant and products.
 * @returns {JSX.Element} The rendered LaundryPrice component.
 */
export const LaundryPrice = ({
  bundle, products, accessories = null, isBundleModified = false
}) => {
  const { defaultVariables } = useContext(QueryContext);
  const isCustomerIdentified = defaultVariables?.current?.isBrandPricingPolicyCompliant || false;

  const isAccessoriesSelected = (accessories?.selectedVariant !== NONE_VALUE && accessories?.products?.length > 0);
  const priceTitle = isAccessoriesSelected ? 'Price for 2 Appliances + Accessories' : 'Price for 2 Appliances';

  const selectedAccessories = useMemo(
    () => (isAccessoriesSelected ? accessories.products : []),
    [isAccessoriesSelected, accessories]
  );

  // isBundleModified makes the hook recompute the total from the swapped
  // item-level pricing instead of the parent bundle rollup.
  const { pricing } = useBundlePricingDetails({
    product: bundle,
    products,
    selectedAccessories,
    isCustomerIdentified,
    isBundleModified,
    loading: false
  });

  const bundlePromotionData = useMemo(() => {
    // BMSM is a bundle-level offer. On pristine load read it from the parent bundle; after a swap
    // the parent rollup is stale, so read the fresh per-item adjustments from the active products.
    const activeAdjustments = isBundleModified
      ? [...(products || []), ...selectedAccessories]
        .flatMap((item) => item?.pricing?.bundlePromotionalAdjustments || [])
      : bundle?.pricing?.bundlePromotionalAdjustments;
    return getBundlePromotionData(activeAdjustments);
  }, [isBundleModified, products, selectedAccessories, bundle?.pricing?.bundlePromotionalAdjustments]);

  const showBundlePromo = !!bundlePromotionData?.product?.pricing?.conditionalPromotions?.length;

  if (!pricing) {
    return null;
  }

  const propProductData = {
    itemId: bundle?.itemId,
    identifiers: bundle?.identifiers,
    pricing: {
      ...pricing,
      unitOfMeasure: pricing?.unitOfMeasure || 'package'
    }
  };

  return (
    <div>
      <div className="sui-mb-2">
        <Typography height="tight" variant="body-base">
          {priceTitle}
        </Typography>
      </div>
      <Price
        itemId={bundle?.itemId}
        product={propProductData}
      />
      {showBundlePromo && (
        <div className="sui-my-4" data-testid="laundry-promo-tag">
          <Promotion itemId={bundle?.itemId} pageType="BIP" promotionData={bundlePromotionData} />
        </div>
      )}
      <LaundryAddToCart bundle={bundle} products={products} accessories={selectedAccessories} />
    </div>
  );
};

LaundryPrice.propTypes = {
  bundle: shapeProps({}).isRequired,
  products: arrayOfProps(shapeProps({})).isRequired,
  accessories: shapeProps({}),
  isBundleModified: boolProps
};

LaundryPrice.dataModel = extend({
  products: params({ itemIds: arrayOf(string().isRequired()).isRequired() }).arrayOf({
    info: shape({
      quantityLimit: string(),
      isInStoreReturnMessageEligible: bool(),
      returnable: string()
    }),
    fulfillment: {
      anchorStoreStatusType: string(),
      backordered: bool(),
      backorderedShipDate: string(),
      bossExcludedShipStates: string(),
      bodfsAssemblyEligible: bool(),
      seasonStatusEligible: bool(),
      excludedShipStates: string(),
      fulfillmentOptions: shape({
        fulfillable: bool(),
        services: shape({
          type: string(),
          isDefault: bool(),
          deliveryCharge: string(),
          deliveryDates: shape({
            endDate: string(),
            startDate: string()
          }),
          deliveryTimeline: string(),
          freeDeliveryThreshold: string(),
          totalCharge: string(),
          locations: shape({
            type: string(),
            isAnchor: bool(),
            locationId: string(),
            storeName: string(),
            storePhone: string(),
            state: string(),
            distance: string(),
            inventory: shape({
              quantity: string(),
              isInStock: bool(),
              isOutOfStock: bool(),
              isLimitedQuantity: bool(),
              isUnavailable: bool(),
              maxAllowedBopisQty: string(),
              minAllowedBopisQty: string()
            })
          })
        }),
        type: string()
      })
    }
  })
},
Price,
Promotion);